import React, { memo, useEffect, useRef, useState } from 'react';
import {
  Stack,
  Card,
  Typography,
  RadioGroup,
  Radio,
  FormControl,
  FormControlLabel,
  TextField,
  Checkbox,
  Button,
  Divider,
  Icon
} from 'investira.react.components';
import NumericFormatCustom from '../../../components/molecules/NumericFormatCustom';
import { AVALIACAO_RESPOSTA_TIPO } from '../../../enums';
import { temValorResposta } from '../_shared/avaliacaoRespostaPayload';

/**
 * Obtém o valor selecionável conforme o contrato de cada tipo de resposta.
 */
const pvObterValorOpcao = (pOpcao, pTipoQuestao) => {
  return pTipoQuestao === 'dicotomica' ? pOpcao?.valor : pOpcao?.id;
};

const QuestaoCardTipo = memo(props => {
  const { data, value = '', index, resposta = {}, onChange } = props;

  const [comentario, setComentario] = useState('');
  const [anexos, setAnexos] = useState([]);
  const [valorTexto, setValorTexto] = useState('');
  const [valoresMultiplos, setValoresMultiplos] = useState([]);
  const inputArquivoRef = useRef(null);

  const { id, questao, resposta_tipo: respostaTipo, resposta_dominio: respostaDominio = [] } = data;
  const xTipoQuestao =
    respostaTipo === AVALIACAO_RESPOSTA_TIPO.MUL.id
      ? 'multipla_escolha_unica'
      : respostaTipo === AVALIACAO_RESPOSTA_TIPO.DIC.id
      ? 'dicotomica'
      : respostaTipo === AVALIACAO_RESPOSTA_TIPO.NUM.id
      ? 'numero'
      : null;
  const xQuestoes = Array.isArray(respostaDominio) ? respostaDominio : [];
  const xRespostaSelecionada = resposta?.resposta_id ?? value ?? '';

  useEffect(() => {
    const xRespostaTexto = resposta?.resposta_texto;
    setComentario(resposta?.comentario || '');
    setAnexos(Array.isArray(resposta?.anexos) ? resposta.anexos : []);

    if (xTipoQuestao === 'multipla_escolha_multipla') {
      const xValores = Array.isArray(xRespostaTexto)
        ? xRespostaTexto
        : String(xRespostaTexto || '')
            .split(';')
            .map(xItem => xItem.trim())
            .filter(Boolean);
      setValoresMultiplos(xValores);
      return;
    }

    if (xTipoQuestao === 'numero' || xTipoQuestao === 'data') {
      setValorTexto(String(xRespostaTexto ?? ''));
    }
  }, [xTipoQuestao, resposta?.resposta_texto, resposta?.comentario, resposta?.anexos]);

  const handleChangeComentario = pEvent => {
    const xComentario = pEvent.target.value;
    setComentario(xComentario);
    onChange?.(id, {
      ...getRespostaAtual(),
      comentario: xComentario
    });
  };

  const handleChangeRespostaUnica = pEvent => {
    const xRespostaId = Number(pEvent.target.value);
    const xRespostaSelecionadaAtual = xQuestoes.find(
      pResposta => Number(pvObterValorOpcao(pResposta, xTipoQuestao)) === xRespostaId
    );
    onChange?.(id, {
      resposta_id: xRespostaId,
      resposta_texto: xRespostaSelecionadaAtual?.label || null
    });
  };

  const handleToggleMultipla = pValor => {
    setValoresMultiplos(pPrev => {
      const xExiste = pPrev.includes(pValor);
      const xProximos = xExiste ? pPrev.filter(xItem => xItem !== pValor) : [...pPrev, pValor];

      onChange?.(id, {
        resposta_id: xProximos.length ? 1 : null,
        resposta_texto: xProximos
      });

      return xProximos;
    });
  };

  const handleChangeTexto = pEvent => {
    const xValor = pEvent.target.value;
    setValorTexto(xValor);

    onChange?.(id, {
      resposta_id: xValor ? 1 : null,
      resposta_texto: xValor || null
    });
  };

  const handleChangeNumero = pEvent => {
    const xValor = pEvent?.target?.value || '';
    setValorTexto(xValor);

    onChange?.(id, {
      resposta_id: xValor ? 1 : null,
      resposta_texto: xValor || null
    });
  };

  const getRespostaAtual = () => {
    if (xTipoQuestao === 'multipla_escolha_multipla') {
      return {
        resposta_id: valoresMultiplos.length ? 1 : null,
        resposta_texto: valoresMultiplos
      };
    }

    if (xTipoQuestao === 'numero' || xTipoQuestao === 'data') {
      return {
        resposta_id: valorTexto ? 1 : null,
        resposta_texto: valorTexto || null
      };
    }

    const xRespostaId = Number(xRespostaSelecionada);
    const xRespostaSelecionadaAtual = xQuestoes.find(
      pResposta => Number(pvObterValorOpcao(pResposta, xTipoQuestao)) === xRespostaId
    );
    const xRespostaValida =
      temValorResposta(xRespostaSelecionada) &&
      Number.isFinite(xRespostaId) &&
      (xTipoQuestao === 'dicotomica' || xRespostaId > 0);

    return {
      resposta_id: xRespostaValida ? xRespostaId : null,
      resposta_texto: xRespostaSelecionadaAtual?.label || null
    };
  };

  const handleAnexarArquivo = () => {
    inputArquivoRef.current?.click();
  };

  const handleArquivoSelecionado = pEvent => {
    const xArquivosSelecionados = Array.from(pEvent.target.files || []);
    if (!xArquivosSelecionados.length) return;

    const xRespostaAtual = getRespostaAtual();

    setAnexos(pPrev => {
      const xProximosAnexos = [...pPrev, ...xArquivosSelecionados];
      onChange?.(id, {
        ...xRespostaAtual,
        comentario,
        anexos: xProximosAnexos,
        anexos_novos: xArquivosSelecionados
      });
      return xProximosAnexos;
    });

    pEvent.target.value = '';
  };

  const xTemRespostaSelecionada = temValorResposta(getRespostaAtual().resposta_id);

  return (
    <Card id={id}>
      <Stack gap={2} py={4}>
        <Stack px={4}>
          <Stack mb={1} direction="row" alignItems="center" justifyContent="space-between">
            <Stack direction="row" gap={1} alignItems="center">
              <Typography variant="caption" color="textSecondary">
                Questão {index + 1}
              </Typography>
            </Stack>
          </Stack>
          <Stack mb={2}>
            <Typography variant="body1" color="textPrimary" sx={{ fontWeight: 600 }}>
              {questao}
            </Typography>
          </Stack>

          {(xTipoQuestao === 'likert' ||
            xTipoQuestao === 'dicotomica' ||
            xTipoQuestao === 'escala_linear') && (
            <FormControl fullWidth>
              <RadioGroup
                name={`questao-${id}`}
                value={String(xRespostaSelecionada ?? '')}
                onChange={handleChangeRespostaUnica}
                row={xTipoQuestao === 'escala_linear'}>
                <Stack gap={2} direction={xTipoQuestao === 'escala_linear' ? 'row' : 'column'}>
                  {xQuestoes.map((xQuestao, xIndex) => (
                    <FormControlLabel
                      id={`questao-${id}-opcao-${xQuestao?.id || xIndex}`}
                      key={`questao-${id}-opcao-${xQuestao?.id || xIndex}`}
                      value={pvObterValorOpcao(xQuestao, xTipoQuestao)}
                      control={<Radio size="small" />}
                      label={
                        <Typography variant="body2" color="textPrimary">
                          {xQuestao?.label || ''}
                        </Typography>
                      }
                    />
                  ))}
                </Stack>
              </RadioGroup>
            </FormControl>
          )}

          {xTipoQuestao === 'multipla_escolha_unica' && (
            <FormControl fullWidth>
              <RadioGroup
                name={`questao-${id}`}
                value={String(xRespostaSelecionada ?? '')}
                onChange={handleChangeRespostaUnica}
                row={xTipoQuestao === 'escala_linear'}>
                <Stack gap={2} direction={xTipoQuestao === 'escala_linear' ? 'row' : 'column'}>
                  {xQuestoes.map((xQuestao, xIndex) => (
                    <FormControlLabel
                      id={`questao-${id}-opcao-${xQuestao?.id || xIndex}`}
                      key={`questao-${id}-opcao-${xQuestao?.id || xIndex}`}
                      value={pvObterValorOpcao(xQuestao, xTipoQuestao)}
                      control={<Radio size="small" />}
                      label={
                        <Typography variant="body2" color="textPrimary">
                          {xQuestao?.label || ''}
                        </Typography>
                      }
                    />
                  ))}
                </Stack>
              </RadioGroup>
            </FormControl>
          )}

          {xTipoQuestao === 'multipla_escolha_multipla' && (
            <FormControl fullWidth>
              <Stack gap={2}>
                {xQuestoes.map((xQuestao, xIndex) => (
                  <FormControlLabel
                    key={`questao-${id}-opcao-multipla-${xIndex + 1}`}
                    control={
                      <Checkbox
                        size="small"
                        checked={valoresMultiplos.includes(xQuestao?.label)}
                        onChange={() => handleToggleMultipla(xQuestao?.label)}
                      />
                    }
                    label={
                      <Typography variant="body2" color="textPrimary">
                        {xQuestao?.label || ''}
                      </Typography>
                    }
                  />
                ))}
              </Stack>
            </FormControl>
          )}

          {xTipoQuestao === 'numero' && (
            <NumericFormatCustom
              customInput={TextField}
              label="Resposta numérica"
              name={`questao-${id}-numero`}
              variant="outlined"
              fullWidth
              size="small"
              type="text"
              value={valorTexto}
              thousandSeparator="."
              inputMode="decimal"
              onChange={handleChangeNumero}
              onWheel={pEvent => pEvent.target.blur()}
            />
          )}

          {xTipoQuestao === 'data' && (
            <TextField
              variant="outlined"
              fullWidth
              size="small"
              type="date"
              label="Data"
              value={valorTexto}
              onChange={handleChangeTexto}
              InputLabelProps={{ shrink: true }}
            />
          )}
        </Stack>

        <Divider />

        {xTemRespostaSelecionada && (
          <Stack px={4} gap={1.5}>
            <TextField
              variant="outlined"
              fullWidth
              multiline
              minRows={3}
              size="small"
              label="Comentário"
              placeholder="Digite um comentário para esta questão"
              value={comentario}
              onChange={handleChangeComentario}
            />
            <input
              ref={inputArquivoRef}
              type="file"
              multiple
              style={{ display: 'none' }}
              onChange={handleArquivoSelecionado}
            />
            <Stack direction="row" justifyContent="flex-end">
              <Button
                id="questao-anexo"
                variant="outlined"
                onClick={handleAnexarArquivo}
                size="small"
                startIcon={<Icon iconName="paper_clip" size={16} />}>
                Anexar arquivo
              </Button>
            </Stack>
            {anexos.length > 0 && (
              <Stack gap={0.5}>
                {anexos.map((xAnexo, xIndex) => (
                  <Typography
                    key={`${xAnexo.name}-${xIndex}`}
                    variant="caption"
                    color="textSecondary">
                    {xAnexo.name}
                  </Typography>
                ))}
              </Stack>
            )}
          </Stack>
        )}
      </Stack>
    </Card>
  );
});

export default QuestaoCardTipo;
